import type { RenderBoxShadow, RenderBoxStroke, RenderImageBox } from '@lite-workspace/shared/render-model';
import type { SceneShadow } from './text-scene-adapter';

/**
 * Photoshop layer effects a photo frame carries into the scene, plus the silhouette they follow.
 *
 * Twin of `FrameDecor` in `engine/psd-render/core/src/scene.rs` — `toSpreadScene` and the Rust
 * `build_scene` must emit byte-identical JSON, so every rule here is mirrored there (`frame_decor_of`
 * in `adapter/mod.rs`) and pinned by the shared golden fixtures.
 *
 * The legacy `stroke`/`strokeWidth` pair on the drawable means something else: the ALBUM-WIDE default
 * outline a frame with no stroke of its own adopts. A frame that owns outlines carries them here.
 */
export interface SceneFrameDecor {
  /** Corner radius in book px; absent = square corners. */
  radius?: number;
  strokes?: SceneFrameStrokeEntry[];
  /** Drop shadows, drawn under the frame before the photo. */
  shadows?: SceneShadow[];
  /** Inner shadows, clipped to the silhouette and drawn over the photo, under the strokes. */
  innerShadows?: SceneShadow[];
  /** Outer glows as zero-offset shadows carrying the effect's own blend (PS default screen). */
  glows?: SceneShadow[];
  /** PSD contour the effects follow when the frame is not a rectangle. */
  clip?: SceneClipKnotLike[];
}

export interface SceneClipKnotLike {
  points: number[];
  linked?: boolean;
}

export interface SceneFrameStrokeEntry {
  color: string;
  width: number;
  opacity: number;
  /** PS "Position": where the line grows from the frame edge. Absent = `inside`. */
  position?: 'outside' | 'center' | 'inside';
  /** `round` — внешний край скругляется на толщину линии даже у прямых углов; absent = острые. */
  corners?: 'round';
  gradient?: SceneStrokeGradient;
}

export interface SceneStrokeGradient {
  colors: string[];
  angle: number;
  reverse: boolean;
}

const live = <T extends { enabled?: boolean }>(list: readonly T[] | undefined): T[] =>
  (list ?? []).filter(e => e.enabled !== false);

/**
 * The frame's own layer effects in book px. `undefined` when the frame carries nothing the legacy
 * `stroke`/`strokeWidth` pair cannot already express — that pair keeps drawing the pixel-snapped ring
 * every existing album was rendered with, and the richer path opens only for what it cannot say:
 * a position other than inside, a second outline, opacity, a gradient, any shadow, a radius, a silhouette.
 * `legacyPair` is false for a carrier with no such pair (an object layer): nothing else would draw its outline.
 */
export function frameDecorOf(
  box: RenderImageBox,
  clip: SceneClipKnotLike[] | undefined,
  masked: boolean,
  legacyPair = true,
): SceneFrameDecor | undefined {
  const radius = Math.max(0, box.cornerRadius ?? 0);
  const strokes: SceneFrameStrokeEntry[] = live(box.effects?.strokes)
    .filter(s => s.width > 0)
    .map(s => ({
      color: s.color,
      width: s.width,
      opacity: s.opacity ?? 1,
      ...(s.position ? { position: s.position } : {}),
      ...(s.corners === 'round' ? { corners: 'round' as const } : {}),
      ...(strokeGradientOf(s) ? { gradient: strokeGradientOf(s) as SceneStrokeGradient } : {}),
    }));
  const allShadows = live(box.effects?.shadows);
  const shadows = allShadows.filter(s => !s.inner).map(toSceneShadow);
  const innerShadows = allShadows.filter(s => s.inner === true).map(toSceneShadow);
  // A glow is a zero-offset shadow whose PS "Choke" plays the part of a shadow's Spread.
  const glows: SceneShadow[] = live(box.effects?.glows)
    .filter(g => !g.inner)
    .map(g => ({
      color: g.color,
      blur: g.blur,
      dx: 0,
      dy: 0,
      opacity: g.opacity ?? 1,
      spread: g.choke ?? 0,
      blend: g.blendMode ?? 'screen',
    }));

  // Nothing to draw beats every other question: a masked frame with no effects must keep the exact
  // scene it had before FKS-12847, down to the absent key.
  if (!strokes.length && !shadows.length && !innerShadows.length && !glows.length && radius === 0) return undefined;

  const legacyCanSayIt =
    legacyPair &&
    !shadows.length &&
    !innerShadows.length &&
    !glows.length &&
    radius === 0 &&
    !clip &&
    !masked &&
    strokes.length === 1 &&
    strokes[0].opacity === 1 &&
    !strokes[0].gradient &&
    (strokes[0].position ?? 'inside') === 'inside';
  if (legacyCanSayIt) return undefined;

  return {
    ...(radius > 0 ? { radius } : {}),
    ...(strokes.length ? { strokes } : {}),
    ...(shadows.length ? { shadows } : {}),
    ...(innerShadows.length ? { innerShadows } : {}),
    ...(glows.length ? { glows } : {}),
    ...(clip ? { clip } : {}),
  };
}

/**
 * A live layer draws its effects itself (`layerStyles`), so its decor keeps only the silhouette: radius and clip.
 * Built from the FULL box first — the effects decide whether a decor exists at all. Twin of Rust `without_live_decor`.
 */
export function withoutLiveDecor(
  box: Pick<RenderImageBox, 'layerStyleRendering'>,
  decor: SceneFrameDecor | undefined,
): SceneFrameDecor | undefined {
  if (box.layerStyleRendering !== 'live' || !decor) return decor;
  const { radius, clip } = decor;
  return radius || clip ? { ...(radius ? { radius } : {}), ...(clip ? { clip } : {}) } : undefined;
}

/** Book px → render px, exactly where the drawable's own geometry is scaled. Percentages never scale. */
export function scaleFrameDecor(decor: SceneFrameDecor, s: number): SceneFrameDecor {
  const shadow = (sh: SceneShadow): SceneShadow => ({
    ...sh,
    blur: sh.blur * s,
    dx: sh.dx * s,
    dy: sh.dy * s,
    ...(sh.distance != null ? { distance: sh.distance * s } : {}),
  });
  return {
    ...decor,
    ...(decor.radius != null ? { radius: decor.radius * s } : {}),
    ...(decor.strokes ? { strokes: decor.strokes.map(st => ({ ...st, width: st.width * s })) } : {}),
    ...(decor.shadows ? { shadows: decor.shadows.map(shadow) } : {}),
    ...(decor.innerShadows ? { innerShadows: decor.innerShadows.map(shadow) } : {}),
    ...(decor.glows ? { glows: decor.glows.map(shadow) } : {}),
    ...(decor.clip ? { clip: decor.clip.map(k => ({ ...k, points: k.points.map(n => n * s) })) } : {}),
  };
}

function toSceneShadow(s: RenderBoxShadow): SceneShadow {
  // Key order = field order of Rust `DropShadow` (serde writes fields as declared), so the JSON is
  // byte-identical, not just equal after normalisation. The six PS shadow parameters ride as they do on
  // text (`pickShadows`); Rust `box_shadow` emits the same six. An absent one is dropped by JSON, as
  // `skip_serializing_if` drops it in Rust.
  return {
    contour: s.contour,
    noise: s.noise,
    antialiased: s.antialiased,
    layerConceals: s.layerConceals,
    angle: s.angle,
    distance: s.distance,
    color: s.color,
    blur: s.blur,
    dx: s.offsetX,
    dy: s.offsetY,
    opacity: s.opacity ?? 1,
    spread: s.spread ?? 0,
    blend: s.blendMode ?? 'multiply',
  };
}

function strokeGradientOf(s: RenderBoxStroke): SceneStrokeGradient | undefined {
  // A gradient without `fillType: 'gradient'` is PS leftovers, not the chosen fill.
  // A frame stroke has no Shape Burst model; it keeps the flat colour.
  if (s.fillType !== 'gradient' || !s.gradient || s.gradient.colors.length < 2) return undefined;
  if (s.gradient.style === 'shapeburst') return undefined;
  return { colors: [...s.gradient.colors], angle: s.gradient.angle ?? 0, reverse: s.gradient.reverse ?? false };
}
